import { useState } from 'react';
import { IonToast, IonSpinner, IonButton } from '@ionic/react';
import { SkillCard } from '@/components/candidate/SkillCard';
import { SkillAutocomplete } from '@/components/candidate/SkillAutocomplete';
import { SkillDetailPanel } from '@/components/candidate/SkillDetailPanel';
import {
  useCandidateSkills,
  useCreateSkill,
  useUpdateSkill,
  useDeleteSkill,
  type SkillData,
} from '@/lib/api/hooks';
import { resolveSkillDescription, type OfoDescription } from '@/lib/ofo-client-search';
import './SkillManager.css';

interface SkillManagerContentProps {
  candidateId: string;
}

export function SkillManagerContent({ candidateId }: SkillManagerContentProps) {
  const { data: skillsEnvelope, isLoading: skillsLoading } = useCandidateSkills(candidateId);
  const skills: SkillData[] = skillsEnvelope?.data ?? [];

  const createSkill = useCreateSkill();
  const updateSkill = useUpdateSkill();
  const deleteSkill = useDeleteSkill();

  const [editing, setEditing] = useState<string | null>(null);
  const [proficiency, setProficiency] = useState<SkillData['proficiency']>('Intermediate');
  const [years, setYears] = useState(1);
  const [toast, setToast] = useState<{ msg: string; color: string } | null>(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailSkill, setDetailSkill] = useState<string>('');
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState<OfoDescription | null>(null);

  const isMutating = createSkill.isPending || updateSkill.isPending || deleteSkill.isPending;

  const handleAddSkill = (name: string, _isCustom: boolean) => {
    if (!candidateId) return;
    if (skills.some((s) => s.name === name)) {
      setToast({ msg: `${name} is already in your skills`, color: 'warning' });
      return;
    }
    createSkill.mutate(
      { candidateId, data: { name, proficiency: 'Intermediate', yearsExperience: 1, status: 'Active' } },
      { onSuccess: () => setToast({ msg: `${name} added`, color: 'success' }) },
    );
  };

  const startEdit = (skill: SkillData) => {
    setEditing(skill._id ?? null);
    setProficiency(skill.proficiency);
    setYears(skill.yearsExperience ?? 1);
  };

  const handleUpdate = () => {
    if (!editing || !candidateId) return;
    updateSkill.mutate(
      { id: editing, data: { proficiency, yearsExperience: years }, candidateId },
      {
        onSuccess: () => {
          setEditing(null);
          setToast({ msg: 'Skill updated', color: 'success' });
        },
      },
    );
  };

  const handleDelete = (skill: SkillData) => {
    if (!skill._id || !candidateId) return;
    deleteSkill.mutate(
      { id: skill._id, rev: (skill as any)._rev, candidateId },
      { onSuccess: () => setToast({ msg: `${skill.name} removed`, color: 'success' }) },
    );
  };

  const handleViewDetail = async (skill: SkillData) => {
    setDetailSkill(skill.name);
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailData(null);
    try {
      const result = await resolveSkillDescription(skill.name);
      setDetailData(result);
    } catch {
      setDetailData(null);
    } finally {
      setDetailLoading(false);
    }
  };

  if (skillsLoading) {
    return (
      <div className="sm-loading">
        <IonSpinner name="crescent" />
      </div>
    );
  }

  return (
    <div className="sm-root">
      <div className="sm-section-header">
        <h3 className="sm-section-title">Your skills</h3>
        <span className="sm-count">{skills.length}</span>
      </div>

      {skills.length > 0 && (
        <div className="sm-list">
          {skills.map((s) => (
            <SkillCard
              key={s._id ?? s.name}
              skill={s}
              onEdit={startEdit}
              onDelete={handleDelete}
              onViewDetail={handleViewDetail}
            />
          ))}
        </div>
      )}

      {skills.length === 0 && !isMutating && (
        <div className="sm-empty">
          <p className="sm-empty-title">No skills yet</p>
          <p className="sm-empty-sub">Add your trade skills to improve your profile visibility and get matched with the right opportunities.</p>
          <IonButton
            size="small"
            fill="outline"
            data-testid="empty-add-skill"
            onClick={() =>
              document
                .querySelector('.sm-add-section')
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }
          >
            Add your first skill
          </IonButton>
        </div>
      )}

      {editing && (
        <div className="sm-edit-card">
          <div className="sm-edit-header">
            <span className="sm-edit-label">Edit {skills.find((s) => s._id === editing)?.name}</span>
          </div>
          <div className="sm-proficiency-row">
            {(['Beginner', 'Intermediate', 'Expert'] as const).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setProficiency(p)}
                className={`sm-prof-btn ${proficiency === p ? 'sm-prof-btn--active' : ''}`}
              >
                {p}
              </button>
            ))}
          </div>
          <div className="sm-years-row">
            <label className="sm-years-label">Years of experience</label>
            <input
              type="number"
              min={0}
              max={50}
              value={years}
              onChange={(e) => setYears(Number(e.target.value))}
              className="sm-years-input"
            />
          </div>
          <div className="sm-edit-actions">
            <button type="button" onClick={() => setEditing(null)} className="sm-btn-cancel">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleUpdate}
              disabled={updateSkill.isPending}
              className="sm-btn-save"
            >
              {updateSkill.isPending ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </div>
      )}

      <div className="sm-add-section">
        <h3 className="sm-add-title">Add a skill</h3>
        <SkillAutocomplete
          existingSkills={skills.map((s) => s.name)}
          onSelect={handleAddSkill}
        />
      </div>

      {isMutating && (
        <div className="sm-mutating">
          <IonSpinner name="crescent" style={{ width: 18, height: 18 }} />
          <span>Saving...</span>
        </div>
      )}

      <SkillDetailPanel
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        loading={detailLoading}
        detail={detailData}
        skillName={detailSkill}
      />

      <IonToast isOpen={!!toast} message={toast?.msg} color={toast?.color} duration={2000} onDidDismiss={() => setToast(null)} />
    </div>
  );
}
